import React, { useState } from "react";

import AlertCircle from "@uui/AlertCircle";
import WarningIcon from "@uui/AlertTriangle";
import CheckCircle from "@uui/CheckCircle";
import InfoCircle from "@uui/InfoCircle";
import CloseIcon from "@uui/XClose";

import {
  Alert as MuiAlert,
  AlertColor,
  AlertProps as MuiAlertProps,
  Box,
  Button,
  ButtonProps,
  Collapse,
  Stack,
  Typography,
} from "@mui/material";
import styled from "@mui/material/styles/styled";

import useTimeout from "@app/common/hooks/useTimeout";
import { lightOrDark } from "@app/embedded/theme/utils";

import IconButton from "@rui/IconButton";

export type DismissButtonProps = Pick<ButtonProps, "color" | "onClick"> & {
  variant: "icon" | "text";
};

interface AlertProps extends MuiAlertProps {
  alignItems?: React.CSSProperties["alignItems"];
  /** timeout in ms */
  autoDismissTimeout?: number;
  /** Embedded alerts are alerts that are embedded within another component. If true, will use
   * our primary font family (among other slight styling differences) */
  embedded?: boolean;
  dismissible?: boolean;
  dismissibleVariant?: "text" | "icon";
  onDismiss?: () => void;
  renderDismissButton?: (props: DismissButtonProps) => React.ReactElement;
  /** If true, will render the dismiss action on the right (renders on left by default) */
  invertActions?: boolean;
  fullWidth?: boolean;
  /** If "row", the actions will display inline with the message. If "column", will appear below the message */
  direction?: "row" | "column";
}

const ICONS: { [key in AlertColor]: React.ReactNode | undefined } = {
  success: <CheckCircle />,
  info: <InfoCircle />,
  warning: <WarningIcon />,
  error: <AlertCircle />,
};

const DismissButton = ({
  color,
  onClick,
  variant = "text",
}: DismissButtonProps) => {
  if (variant === "text") {
    return (
      <Button
        variant="text"
        color={color}
        sx={{ height: "auto", mr: 2 }}
        onClick={onClick}
      >
        Dismiss
      </Button>
    );
  }

  return (
    <IconButton onClick={onClick} size="small">
      <CloseIcon sx={{ fontSize: "18px" }} />
    </IconButton>
  );
};

type StyledAlertProps = MuiAlertProps & { embedded: boolean; title?: string };

const StyledAlert = styled(({ embedded: _, ...props }: StyledAlertProps) => (
  <MuiAlert {...props} />
))(({ embedded, theme }) => ({
  width: "100%",
  color: lightOrDark(
    theme.palette.shades.neutral[800],
    theme.palette.shades.neutral[200]
  )(theme),
  padding: theme.spacing(embedded ? 1 : 1.5, 1),
  "& .MuiAlert-message": {
    padding: 0,
  },
  "& .MuiAlert-action": {
    display: "flex",
    alignItems: "center",
    padding: 0,
    marginRight: 0,
    alignSelf: "center",
  },
  "& .MuiAlert-icon": {
    margin: embedded ? theme.spacing(0, 1, 0, 0.5) : undefined,
  },
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(embedded ? 1 : 1.5, embedded ? 1 : 3),
  },
  fontFamily: embedded
    ? theme.typography.fontFamily
    : theme.typography.fontFamilySecondary,
}));

/** @deprecated use AlertBanner or InfoPanel instead! */
const Alert = ({
  alignItems = "center",
  direction = "row",
  autoDismissTimeout = 0,
  embedded = false,
  invertActions,
  severity,
  action,
  dismissible,
  dismissibleVariant = "text",
  onDismiss,
  children,
  renderDismissButton = DismissButton,
  fullWidth = true,
  title,
  sx,
  ...props
}: AlertProps) => {
  const [alertOpen, setAlertOpen] = useState(true);
  const icon =
    typeof props.icon === "undefined"
      ? severity
        ? ICONS[severity]
        : undefined
      : props.icon;

  const handleDismissAlert = () => {
    setAlertOpen(false);
    onDismiss?.();
  };

  useTimeout(
    handleDismissAlert,
    autoDismissTimeout,
    alertOpen && autoDismissTimeout > 0,
    [alertOpen]
  );

  return (
    <Collapse
      in={alertOpen}
      unmountOnExit
      sx={{
        width: fullWidth ? "100%" : undefined,
        flexShrink: 0,
      }}
    >
      <StyledAlert
        embedded={embedded}
        severity={severity}
        icon={icon}
        sx={{ alignItems, ...sx }}
        action={
          direction === "row" ? (
            <Stack
              direction={invertActions ? "row-reverse" : "row"}
              spacing={2}
              sx={{ ml: "auto" }}
            >
              {dismissible &&
                dismissibleVariant === "text" &&
                renderDismissButton({
                  onClick: handleDismissAlert,
                  color: severity,
                  variant: dismissibleVariant,
                })}
              {action}
              {dismissible &&
                dismissibleVariant === "icon" &&
                renderDismissButton({
                  onClick: handleDismissAlert,
                  color: severity,
                  variant: dismissibleVariant,
                })}
            </Stack>
          ) : undefined
        }
        {...props}
      >
        <Box sx={{ flex: 1, pr: { xs: 0, sm: action || dismissible ? 3 : 0 } }}>
          {title && (
            <Typography
              variant="bodySecondaryMed"
              component="p"
              color="shades.neutral.800"
            >
              {title}
            </Typography>
          )}
          {children}
          {direction === "column" && (
            <Stack
              direction={invertActions ? "row-reverse" : "row"}
              spacing={2}
              alignItems="center"
              justifyContent="start"
              paddingTop={1}
            >
              {dismissible &&
                dismissibleVariant === "text" &&
                renderDismissButton({
                  onClick: handleDismissAlert,
                  color: severity,
                  variant: dismissibleVariant,
                })}
              {action}
              {dismissible &&
                dismissibleVariant === "icon" &&
                renderDismissButton({
                  onClick: handleDismissAlert,
                  color: severity,
                  variant: dismissibleVariant,
                })}
            </Stack>
          )}
        </Box>
      </StyledAlert>
    </Collapse>
  );
};

export default Alert;
